import { useState, type FormEvent } from 'react';
import { api } from '../../lib/api.ts';
import { Archive, Folder, Pencil, Trash, ZoomIn } from '../../lib/icons.tsx';
import AssetPreviewModal from '../AssetPreviewModal.tsx';
import PersonRenameDecksModal from './PersonRenameDecksModal.tsx';
import { isArchived, labelFor, leafOf, type BankActions, type BankItemPatch } from './bankShared.ts';
import type { BankItem } from '../../../shared/deck.ts';

/**
 * The asset card grid — shared by the category view, the "All assets" overview
 * and search results. Owns only which card has its inline editor open; every
 * mutation belongs to the surface above (passed in via `actions`).
 */
export default function BankGrid({
  items,
  categoryKey = null,
  showCategory = false,
  busy,
  actions,
}: {
  items: BankItem[];
  categoryKey?: string | null;
  showCategory?: boolean;
  busy?: boolean;
  actions: BankActions;
}) {
  const { run, reload, pushToast, setMoveItem, setItemStatus, setConfirm } = actions;
  // The item id whose inline editor is open.
  const [editing, setEditing] = useState<string | null>(null);
  // Index into `items` — this grid is one category (or one search result set),
  // so the viewer's arrows walk exactly the group being shown.
  const [magnified, setMagnified] = useState<number | null>(null);
  // The person just renamed, while the deck follow-up is open.
  const [renamed, setRenamed] = useState<{ item: BankItem; from: string; to: string } | null>(null);

  function onDelete(item: BankItem) {
    setConfirm({
      title: `Remove "${item.label}" from the bank?`,
      body: (
        <>
          <p>Its file is deleted for good. Decks that already use it keep their copy.</p>
          <p className="muted bank__note">
            To retire it without losing the file, archive it instead: archived assets stay in the bank but are hidden
            from the pickers and never offered by the AI.
          </p>
        </>
      ),
      danger: true,
      confirmLabel: 'Delete',
      // Offering the non-destructive path right where the destructive one is
      // chosen: archiving is what most "remove this" intentions actually mean.
      secondaryLabel: isArchived(item) ? null : 'Archive instead',
      onSecondary: isArchived(item)
        ? null
        : () => {
            setConfirm(null);
            setItemStatus(item, 'archived');
          },
      onConfirm: async () => {
        await run(async () => {
          await api.delete(`/api/bank/items/${item.id}`);
          await reload();
          pushToast(`Deleted "${item.label}"`);
        }, 'Delete failed');
        setConfirm(null);
      },
    });
  }

  async function saveEdit(item: BankItem, patch: BankItemPatch) {
    await run(async () => {
      await api.put(`/api/bank/items/${item.id}`, patch);
      setEditing(null);
      await reload();
    }, 'Save failed');
  }

  /** A person's name is not an ordinary label edit. Renaming through the plain
   *  PUT keeps the id but records nothing, so the old name stops resolving in
   *  chat and the next team sync reads the change as a leaver plus a joiner.
   *  This routes it through the rename verb instead, which files the old name,
   *  and then offers the decks still printing it. */
  async function saveRename(item: BankItem, nextName: string, rest: BankItemPatch) {
    const was = item.meta?.name || item.label;
    await run(async () => {
      await api.put(`/api/bank/items/${item.id}`, rest);
      const { previous } = await api.post<{ previous: { label: string; meta?: BankItem['meta'] } }>(
        `/api/bank/items/${item.id}/rename`,
        { name: nextName },
      );
      setEditing(null);
      await reload();
      pushToast(`"${was}" is now "${nextName}"`, {
        undo: async () => {
          await api.put(`/api/bank/items/${item.id}`, {
            label: previous.label,
            meta: { name: previous.meta?.name || previous.label, formerNames: previous.meta?.formerNames || [] },
          });
          await reload();
        },
      });
      setRenamed({ item, from: was, to: nextName });
    }, 'Name change failed');
  }

  if (!items.length) return null;

  return (
    <>
      <ul className="assets__grid bank__grid" role="list">
        {items.map((it, i) => {
          const archived = isArchived(it);
          const isPerson = (categoryKey || it.category) === 'people';
          return (
            <li key={it.id} className={'asset-card bank-card' + (archived ? ' bank-card--archived' : '')} title={it.id}>
              <div className="asset-card__thumb bank-card__thumb">
                <img src={`/api/bank/file/${it.id}`} alt={it.label} loading="lazy" />
                {archived && <span className="bank-card__badge">Archived</span>}
                {/* Valid here as a child: unlike the picking room's tile, this
                  thumb is a plain div, not a button. */}
                <button
                  type="button"
                  className="asset-magnify"
                  title="Take a closer look"
                  aria-label={`Preview ${it.label}`}
                  onClick={() => setMagnified(i)}
                >
                  <ZoomIn />
                </button>
              </div>
              {editing === it.id ? (
                <BankCardEditor
                  item={it}
                  isPerson={isPerson}
                  busy={busy}
                  onSave={(patch) => saveEdit(it, patch)}
                  onRename={(name, rest) => saveRename(it, name, rest)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div className="asset-card__meta bank-card__meta">
                  <div className="bank-card__labels">
                    <span className="asset-card__name">{isPerson ? it.meta?.name || it.label : it.label}</span>
                    {isPerson && <span className="muted bank-card__role">{it.meta?.role || 'no role set'}</span>}
                    {isPerson && (it.meta?.departments?.length || 0) > 0 && (
                      <span className="muted bank-card__depts">{it.meta?.departments?.join(' · ')}</span>
                    )}
                    {isPerson && (it.meta?.formerNames?.length || 0) > 0 && (
                      <span className="muted bank-card__depts">formerly {it.meta?.formerNames?.join(', ')}</span>
                    )}
                    {showCategory && <span className="muted bank-card__depts">{labelFor(leafOf(it.category))}</span>}
                    {(it.tags?.length || 0) > 0 && (
                      <span className="bank-card__tags">
                        {(it.tags ?? []).map((t) => (
                          <span key={t} className="tag-chip tag-chip--static">
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <span className="bank-card__actions">
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => setEditing(it.id)}
                      aria-label={`Edit ${it.label}`}
                      title="Edit"
                      disabled={busy}
                    >
                      <Pencil />
                    </button>
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => setMoveItem(it)}
                      aria-label={`Move ${it.label} to another category`}
                      title="Move to category…"
                      disabled={busy}
                    >
                      <Folder />
                    </button>
                    <button
                      type="button"
                      className={'iconbtn' + (archived ? ' is-active' : '')}
                      onClick={() => setItemStatus(it, archived ? 'active' : 'archived')}
                      aria-label={archived ? `Restore ${it.label}` : `Archive ${it.label}`}
                      title={archived ? 'Restore to the bank' : 'Archive: keeps the file, hides it from the AI'}
                      disabled={busy}
                    >
                      <Archive />
                    </button>
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => onDelete(it)}
                      aria-label={`Delete ${it.label}`}
                      title="Delete"
                      disabled={busy}
                    >
                      <Trash />
                    </button>
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {renamed && (
        <PersonRenameDecksModal
          itemId={renamed.item.id}
          from={renamed.from}
          to={renamed.to}
          onClose={() => setRenamed(null)}
          onDone={(updated, skipped) => {
            setRenamed(null);
            pushToast(
              skipped
                ? `Updated ${updated} slide${updated === 1 ? '' : 's'}, ${skipped} could not be changed`
                : `Updated ${updated} slide${updated === 1 ? '' : 's'}`,
            );
          }}
        />
      )}

      {magnified != null && items.length > 0 && (
        <AssetPreviewModal
          items={items.map((it) => ({
            key: it.id,
            src: `/api/bank/file/${encodeURIComponent(it.id)}`,
            kind: 'image',
            label: it.label,
          }))}
          index={Math.min(magnified, items.length - 1)}
          onClose={() => setMagnified(null)}
        />
      )}
    </>
  );
}

function BankCardEditor({
  item,
  isPerson,
  busy,
  onSave,
  onRename,
  onCancel,
}: {
  item: BankItem;
  isPerson: boolean;
  busy?: boolean;
  onSave: (patch: BankItemPatch) => void;
  onRename: (name: string, rest: BankItemPatch) => void;
  onCancel: () => void;
}) {
  const was = isPerson ? item.meta?.name || item.label : item.label;
  const [name, setName] = useState(was);
  const [role, setRole] = useState(item.meta?.role || '');
  // Comma-separated in the inputs; the server normalises both to arrays.
  const [departments, setDepartments] = useState((item.meta?.departments || []).join(', '));
  const [tags, setTags] = useState((item.tags || []).join(', '));
  // A person's name is the one field here that other things point at, so
  // changing it is a different verb from editing their role.
  const renaming = isPerson && name.trim() !== was && !!name.trim();
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!isPerson) {
      onSave({ label: name, tags });
      return;
    }
    const rest: BankItemPatch = { meta: { role, departments }, tags };
    if (renaming) onRename(name.trim(), rest);
    else onSave({ label: name, meta: { name, role, departments }, tags });
  }
  return (
    <form className="bank-card__edit" onSubmit={submit}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={isPerson ? 'Name' : 'Label'}
        aria-label="Name"
      />
      {renaming && (
        <p className="muted bank__note">
          {was} keeps working when anyone asks for them by that name, and you can update the decks that still show it.
          Their photo and their place in every existing deck are untouched.
        </p>
      )}
      {isPerson && (
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Role, e.g. Senior Developer"
          aria-label="Role"
        />
      )}
      {isPerson && (
        <input
          value={departments}
          onChange={(e) => setDepartments(e.target.value)}
          placeholder="Departments, e.g. Digital, PR"
          aria-label="Departments"
        />
      )}
      <input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="Tags, e.g. on-dark, mono"
        aria-label="Tags"
        title="Describes the variant. The AI reads these when choosing between versions of the same asset."
      />
      <div className="bank-card__edit-actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !name.trim()}>
          {renaming ? 'Change name' : 'Save'}
        </button>
      </div>
    </form>
  );
}
